import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { OriginalsNav } from '@/components/OriginalsNav';
import { useCart } from '@/context/CartContext';
import { CheckCircle, XCircle, Loader2, Package } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/orders/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrder(data.order);
        clearCart(); // Clear cart on successful payment
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    }
  }, [sessionId, clearCart]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="order-confirmation">
      <OriginalsNav />
      
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'loading' && (
            <div className="animate-fade-in" data-testid="loading-state">
              <div className="w-24 h-24 rounded-full border border-[#27272a] flex items-center justify-center mx-auto mb-8">
                <Loader2 className="w-10 h-10 text-[#d4af37] animate-spin" />
              </div>
              <h1 className="font-playfair text-3xl text-white mb-4">Processing Your Order</h1>
              <p className="font-cormorant text-lg text-[#a1a1aa]">
                Please wait while we confirm your payment...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-fade-in" data-testid="success-state">
              <div className="w-24 h-24 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h1 className="font-playfair text-3xl lg:text-4xl text-white mb-4">
                Thank You for Your Order!
              </h1>
              <p className="font-cormorant text-lg text-[#a1a1aa] mb-8">
                Your payment was successful. We&apos;ll begin preparing your order right away.
              </p>

              {order && (
                <div className="bg-[#171717] rounded-lg border border-[#27272a] p-8 text-left mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Package className="w-5 h-5 text-[#d4af37]" />
                    <span className="font-cinzel text-xs tracking-widest text-[#d4af37]">ORDER DETAILS</span>
                  </div>

                  <div className="space-y-4 mb-6">
                    {order.items?.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span className="font-cormorant text-[#a1a1aa]">
                          {item.name} × {item.quantity}
                        </span>
                        <span className="font-mono text-white">£{Math.floor(item.subtotal || 0)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-[#27272a] pt-4">
                    <div className="flex justify-between">
                      <span className="font-cinzel tracking-wider text-white">Total Paid</span>
                      <span className="font-mono text-xl text-[#d4af37]">£{Math.floor(order.total || 0)}</span>
                    </div>
                  </div>
                </div>
              )}

              <Link 
                to="/originals"
                className="btn-originals inline-flex items-center gap-3 px-8 py-4 rounded-full font-cinzel tracking-wider"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </Link>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div className="animate-fade-in" data-testid="error-state">
              <div className="w-24 h-24 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-8">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="font-playfair text-3xl text-white mb-4">
                {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
              </h1>
              <p className="font-cormorant text-lg text-[#a1a1aa] mb-8">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : status === 'timeout'
                  ? 'Payment verification timed out. Please check your email for confirmation.'
                  : 'There was an issue processing your payment. Please try again.'}
              </p>
              <Link 
                to="/originals/cart"
                className="btn-originals inline-flex items-center gap-3 px-8 py-4 rounded-full font-cinzel tracking-wider"
                data-testid="return-to-cart-btn"
              >
                Return to Cart
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="font-cormorant text-sm text-[#a1a1aa] text-center">
            © 2024 Ben Jefferies Originals. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
